/**
 * RWBR — RACE AND TEAM MARKS.
 *
 * Howard, 17 Aug 2026:
 *
 *   "Lets implement the logo uploader for the races"
 *   "Lets add an uploader for the team names AND can we have those logos show
 *    on the hood's of the appropriate cars on the race grid and screens?"
 *
 * This is `artStore.ts` with a different noun, and the parts that are the same
 * are the same on purpose: reads go straight to Supabase with the publishable
 * key, writes go through the Worker with the session token, and the whole table
 * is cached once because a 43-car grid asks for a mark forty-three times per
 * render. The art path has already had its bugs found; a second pipeline
 * invented from scratch would be a second set to find.
 *
 * ===========================================================================
 * WHAT A MARK IS ATTACHED TO — and why the two answers are different
 * ===========================================================================
 *
 * A RACE MARK carries `race_key` — `${trackId}#${variant}`, the key
 * `domain/schedule.ts` builds from the static track card and the race variant.
 * It is identical in season 1 and season 9, because it is derived from the
 * calendar and not from any season's instance of it. The Daytona 500 is the
 * Daytona 500 forever, so its crest is uploaded once.
 *
 * A TEAM MARK carries `team_name`, and NOT `teamId`. This looks like the
 * sloppier choice and is the correct one:
 *
 *   `teamId` is `tm-01`, `tm-02`, … — minted by `buildField` in charter order.
 *   `tm-01` is a DIFFERENT SHOP IN EVERY LEAGUE, so a logo hung on it would
 *   follow a slot rather than a team, and a mark uploaded for Hargrove Racing
 *   would reappear in the next career on whichever unrelated shop happened to
 *   be chartered first. That is not a logo, it is a coincidence.
 *
 *   The NAME is the identity a person recognises — it is what the garage
 *   screen, the boards and the dossier print — and it is what Howard named
 *   ("an uploader for the team names"). Names are generated per league from
 *   surnames and trades, so a mark simply does not show in a league that never
 *   produced that name. That is the honest failure: absent, not wrong.
 *
 * Matched CASE-INSENSITIVELY and trimmed, because "Hargrove Racing" typed into
 * an upload form at midnight and "hargrove racing " off a roster are the same
 * shop, and a mark that silently does not appear is indistinguishable from a
 * mark that failed to upload.
 */

import { useEffect, useState } from 'react';
import { supabase } from './supabase.ts';

/** The bucket. Public read; writes only via the Worker's secret key. */
const BUCKET = 'branding';

/** D-026's three marks. 'track' — a track's own crest — landed 4 Oct 2026. */
export type LogoKind = 'race' | 'team' | 'track';

/** A row of `public.event_logos`, in the shape the browser wants it. */
export interface StoredLogo {
  id: string;
  storagePath: string;
  label: string;
  kind: LogoKind;
  /** `${trackId}#${variant}` for a race mark; null otherwise. */
  raceKey: string | null;
  /** The shop's name for a team mark; null otherwise. */
  teamName: string | null;
  /** The track card id for a track mark; null otherwise. */
  trackId: string | null;
  width: number | null;
  height: number | null;
  mimeType: string | null;
  bytes: number | null;
  accentHex: string | null;
  uploadedAt: string | null;
  /** The public URL, built here so no caller has to know the bucket. */
  url: string;
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

let cache: StoredLogo[] | null = null;
let inFlight: Promise<StoredLogo[]> | null = null;

function publicUrl(storagePath: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
}

function rowToLogo(r: Record<string, unknown>): StoredLogo {
  const storagePath = String(r.storage_path ?? '');
  return {
    id: String(r.id ?? storagePath),
    storagePath,
    label: String(r.label ?? 'untitled'),
    kind: r.kind === 'team' ? 'team' : r.kind === 'track' ? 'track' : 'race',
    raceKey: typeof r.race_key === 'string' ? r.race_key : null,
    teamName: typeof r.team_name === 'string' ? r.team_name : null,
    trackId: typeof r.track_id === 'string' ? r.track_id : null,
    width: r.width === null || r.width === undefined ? null : Number(r.width),
    height: r.height === null || r.height === undefined ? null : Number(r.height),
    mimeType: typeof r.mime_type === 'string' ? r.mime_type : null,
    bytes: r.bytes === null || r.bytes === undefined ? null : Number(r.bytes),
    accentHex: typeof r.accent_hex === 'string' ? r.accent_hex : null,
    uploadedAt: typeof r.uploaded_at === 'string' ? r.uploaded_at : null,
    url: publicUrl(storagePath),
  };
}

/**
 * Every mark, once per browser.
 *
 * A FAILURE IS AN EMPTY LIBRARY, NOT AN ERROR. Marks are decoration: a race
 * that cannot reach its crest should still run. The one thing that must never
 * happen is a failed logo fetch taking down a race screen, so this resolves to
 * `[]` and says nothing. (The UPLOADER reports failures loudly — there, silence
 * would be the bug.)
 */
export async function loadLogos(): Promise<readonly StoredLogo[]> {
  if (cache) return cache;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const { data, error } = await supabase.from('event_logos').select('*');
      if (error) throw error;
      cache = (data ?? []).map((r) => rowToLogo(r as Record<string, unknown>));
      return cache;
    } catch {
      cache = [];
      return cache;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Throw the cache away, so the next read sees a write that just landed. */
export function invalidate(): void {
  cache = null;
}

/** What is loaded already, without waiting. Empty before the first load. */
export function cached(): readonly StoredLogo[] {
  return cache ?? [];
}

// ---------------------------------------------------------------------------
// Looking one up
// ---------------------------------------------------------------------------

/** Pure. Trimmed and lowercased — see the note at the top on why. */
export const teamKey = (name: string): string => name.trim().toLowerCase();

/**
 * A pair of indexes over a set of marks. Pure, and built once per load rather
 * than scanned per car: a 43-car grid asks 43 times per render, and a linear
 * scan of the library each time is how a grid starts dropping frames.
 */
export interface LogoIndex {
  byRaceKey: ReadonlyMap<string, StoredLogo>;
  byTeamName: ReadonlyMap<string, StoredLogo>;
  byTrackId: ReadonlyMap<string, StoredLogo>;
  all: readonly StoredLogo[];
}

export function indexLogos(logos: readonly StoredLogo[]): LogoIndex {
  const byRaceKey = new Map<string, StoredLogo>();
  const byTeamName = new Map<string, StoredLogo>();
  const byTrackId = new Map<string, StoredLogo>();
  for (const logo of logos) {
    if (logo.kind === 'race' && logo.raceKey) byRaceKey.set(logo.raceKey, logo);
    if (logo.kind === 'team' && logo.teamName) byTeamName.set(teamKey(logo.teamName), logo);
    if (logo.kind === 'track' && logo.trackId) byTrackId.set(logo.trackId, logo);
  }
  return { byRaceKey, byTeamName, byTrackId, all: logos };
}

export const EMPTY_INDEX: LogoIndex = {
  byRaceKey: new Map(),
  byTeamName: new Map(),
  byTrackId: new Map(),
  all: [],
};

/** Pure. A track's own mark, or null. */
export const logoForTrack = (index: LogoIndex, trackId: string | null | undefined): StoredLogo | null =>
  (trackId ? index.byTrackId.get(trackId) : undefined) ?? null;

/**
 * D-026 — THE RACE HEADER BAND'S TINT. The race crest's accent colour when it
 * has one, else the track mark's; null leaves the band its default colour.
 * Validated here as well as in the database, because it goes into a style.
 */
export function headerAccent(index: LogoIndex, raceKey: string | null | undefined, trackId?: string | null): string | null {
  for (const mark of [logoForRace(index, raceKey), logoForTrack(index, trackId)]) {
    const hex = mark?.accentHex;
    if (hex && /^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  }
  return null;
}

/** Pure. The crest for a named race, or null. */
export const logoForRace = (index: LogoIndex, raceKey: string | null | undefined): StoredLogo | null =>
  (raceKey ? index.byRaceKey.get(raceKey) : undefined) ?? null;

/** Pure. The mark for a shop, by name, or null. */
export const logoForTeam = (index: LogoIndex, teamName: string | null | undefined): StoredLogo | null =>
  (teamName ? index.byTeamName.get(teamKey(teamName)) : undefined) ?? null;

// ---------------------------------------------------------------------------
// The hook every screen uses
// ---------------------------------------------------------------------------

/**
 * The marks, as an index, loaded once for the tab.
 *
 * A HOOK RATHER THAN A PROP THREADED FROM `App.tsx`. The three places a car is
 * drawn — the grid, the boards and the dossier — are four to six components
 * deep and none of the components in between has any business knowing about
 * logos. Threading it would put a `logos` prop on a dozen signatures that do
 * not use it, which is how a prop ends up dropped in one branch and the crest
 * mysteriously missing on one screen.
 *
 * The module cache makes this cheap: every caller after the first gets the same
 * array with no request at all.
 */
export function useLogos(): LogoIndex {
  const [index, setIndex] = useState<LogoIndex>(() =>
    cache ? indexLogos(cache) : EMPTY_INDEX,
  );
  useEffect(() => {
    let alive = true;
    void loadLogos().then((logos) => {
      if (alive) setIndex(indexLogos(logos));
    });
    return () => {
      alive = false;
    };
  }, []);
  return index;
}

// ---------------------------------------------------------------------------
// Writing — through the Worker, as a signed-in owner
// ---------------------------------------------------------------------------

/**
 * Every write carries the session's access token and nothing else.
 *
 * Hiding the uploader from everyone but the owner stops a tab appearing. It
 * does not stop a request: these endpoints are named in the bundle and anyone
 * can call them with curl. The real gate is `requireOwner` in the Worker, and
 * it has to be told who is asking. Signed out sends NO header rather than an
 * empty one, so "not signed in" and "your token was rejected" stay different
 * facts with different answers.
 */
async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export class LogoWriteError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly reason: string | null,
  ) {
    super(message);
    this.name = 'LogoWriteError';
  }
}

/** Does this failure mean "sign in again"? */
export const needsSignIn = (e: unknown): boolean => e instanceof LogoWriteError && e.status === 401;
/** Does it mean "you are somebody else"? Signing in again changes nothing. */
export const isNotOwner = (e: unknown): boolean => e instanceof LogoWriteError && e.status === 403;

async function post<T>(path: string, body: BodyInit, headers?: HeadersInit): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    body,
    headers: { ...(headers as Record<string, string> | undefined), ...(await authHeaders()) },
  });
  let payload: { ok?: boolean; error?: string; reason?: string } & Record<string, unknown>;
  try {
    payload = await res.json();
  } catch {
    throw new LogoWriteError(
      `${path} returned ${res.status} and not JSON — is the Worker deployed?`,
      res.status,
      null,
    );
  }
  if (!res.ok || payload.ok === false) {
    // The Worker's own sentence, verbatim. It knows what went wrong and has
    // already written the honest version.
    throw new LogoWriteError(
      payload.error ?? `${path} failed with ${res.status}`,
      res.status,
      payload.reason ?? null,
    );
  }
  return payload as T;
}

export interface LogoUploadResult {
  storagePath: string;
  url: string;
  originalName: string | null;
  bytes: number;
  mimeType: string;
}

export async function uploadLogo(file: File): Promise<LogoUploadResult> {
  const form = new FormData();
  form.append('file', file);
  return post<LogoUploadResult>('/api/logo/upload', form);
}

export interface SaveLogoInput {
  storagePath: string;
  label: string;
  kind: LogoKind;
  raceKey?: string | null;
  teamName?: string | null;
  trackId?: string | null;
  /** `#rrggbb` — tints the race header band (D-026). */
  accentHex?: string | null;
  width?: number | null;
  height?: number | null;
  bytes?: number | null;
  mimeType?: string | null;
}

export async function saveLogo(input: SaveLogoInput): Promise<StoredLogo | null> {
  const r = await post<{ logo: Record<string, unknown> | null }>(
    '/api/logo/save',
    JSON.stringify(input),
    { 'Content-Type': 'application/json' },
  );
  invalidate();
  return r.logo ? rowToLogo(r.logo) : null;
}

/** Take a mark off whatever it is on, keeping the file. */
export async function unassignLogo(storagePath: string): Promise<void> {
  await post('/api/logo/unassign', JSON.stringify({ storagePath }), {
    'Content-Type': 'application/json',
  });
  invalidate();
}

export async function deleteLogo(storagePath: string): Promise<void> {
  await post('/api/logo/delete', JSON.stringify({ storagePath }), {
    'Content-Type': 'application/json',
  });
  invalidate();
}

export interface LogoHealth {
  canWrite: boolean;
  maxBytes: number;
  accepts: string[];
  reason?: string;
}

/**
 * Can the Worker save a mark at all? Asked BEFORE a drop zone is offered, so
 * "this cannot be saved" is said up front rather than after a file has been
 * dragged in, read and previewed.
 */
export async function logoHealth(): Promise<LogoHealth> {
  try {
    const res = await fetch('/api/logo/health');
    const body = (await res.json()) as LogoHealth & { ok?: boolean };
    return body;
  } catch {
    return {
      canWrite: false,
      maxBytes: 0,
      accepts: [],
      reason: 'The Worker could not be reached, so marks cannot be uploaded from here right now.',
    };
  }
}

/**
 * The natural size of an image file, read in the browser before upload.
 *
 * SVG IS THE AWKWARD ONE and is handled rather than special-cased away: a
 * vector has no intrinsic pixel size in some browsers, so `naturalWidth` comes
 * back 0. Nulls are stored instead of zeros, because a stored 0 would be read
 * downstream as "this image is zero pixels wide" rather than "nobody knows",
 * and something would eventually divide by it.
 */
export function readDimensions(file: File): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const done = (width: number | null, height: number | null): void => {
      URL.revokeObjectURL(url);
      resolve({ width, height });
    };
    img.onload = () => done(img.naturalWidth || null, img.naturalHeight || null);
    img.onerror = () => done(null, null);
    img.src = url;
  });
}
